import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { targetsAPI, employeesAPI } from '@/lib/api';
import { 
  Target, TrendingUp, Users, DollarSign, Calendar,
  Loader2, Settings, ChevronDown, Bell, Send, AlertTriangle
} from 'lucide-react';

export default function StaffTargetsManager({ user }) {
  const [loading, setLoading] = useState(true);
  const [targetsData, setTargetsData] = useState(null);
  const [pendingFees, setPendingFees] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showSetTargets, setShowSetTargets] = useState(false);
  const [bulkTargetAmount, setBulkTargetAmount] = useState('');
  const [individualTarget, setIndividualTarget] = useState({ employeeId: '', amount: '' });
  const [processing, setProcessing] = useState(false);
  const [selectedReminderIds, setSelectedReminderIds] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [confirmBulkOpen, setConfirmBulkOpen] = useState(false);
  const [remindingId, setRemindingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [targetsRes, pendingRes] = await Promise.all([
        targetsAPI.getAllTargets(selectedMonth),
        targetsAPI.getPendingFees()
      ]);
      setTargetsData(targetsRes.data);
      setPendingFees(pendingRes.data);
    } catch (error) {
      console.error('Error fetching targets:', error);
      toast.error('Failed to load targets data');
    } finally {
      setLoading(false);
    }
  };

  const handleSetBulkTargets = async () => {
    if (!bulkTargetAmount || parseFloat(bulkTargetAmount) <= 0) {
      toast.error('Please enter a valid target amount');
      return;
    }
    try {
      setProcessing(true);
      const res = await targetsAPI.setBulkTargets(parseFloat(bulkTargetAmount), selectedMonth);
      toast.success(res.data.message);
      setBulkTargetAmount('');
      setShowSetTargets(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set targets');
    } finally {
      setProcessing(false);
    }
  };

  const handleSetIndividualTarget = async () => {
    if (!individualTarget.employeeId || !individualTarget.amount || parseFloat(individualTarget.amount) <= 0) {
      toast.error('Please select employee and enter valid amount');
      return;
    }
    try {
      setProcessing(true);
      const res = await targetsAPI.setTarget(individualTarget.employeeId, parseFloat(individualTarget.amount), selectedMonth);
      toast.success(res.data.message);
      setIndividualTarget({ employeeId: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set target');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendReminder = async (studentId) => {
    try {
      setRemindingId(studentId);
      const res = await targetsAPI.sendReminder(studentId);
      const emailSent = res.data?.email_sent;
      toast.success(`${res.data?.message || 'Reminder logged'}${emailSent ? ' · email delivered' : ' · email NOT delivered'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  const toggleSelectAll = () => {
    const ids = (pendingFees?.students || []).map((s) => s.student_id);
    setSelectedReminderIds(selectedReminderIds.length === ids.length ? [] : ids);
  };

  const toggleSelectOne = (id) => {
    setSelectedReminderIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkRemind = async () => {
    try {
      setBulkSending(true);
      const ids = selectedReminderIds.length ? selectedReminderIds : null;
      const res = await targetsAPI.sendRemindersBulk(ids);
      const { attempted = 0, sent = 0, email_failed = 0, queued = false } = res.data || {};
      if (queued) {
        toast.success(`Queued ${attempted} reminders for background dispatch — check back in a minute.`);
      } else {
        toast.success(`Bulk reminders: ${sent}/${attempted} email delivered, ${email_failed} email failed`);
      }
      setSelectedReminderIds([]);
      setConfirmBulkOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk reminder failed');
    } finally {
      setBulkSending(false);
    }
  };

  // Generate month options (last 6 months + next 3 months)
  const monthOptions = [];
  const now = new Date();
  for (let i = -6; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = d.toISOString().slice(0, 7);
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading targets...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-targets-manager">
      {/* Header with Month Selector */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {user?.is_super_admin && (
          <Dialog open={showSetTargets} onOpenChange={setShowSetTargets}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="set-targets-btn">
                <Settings className="w-4 h-4 mr-2" /> Set Targets
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Monthly Targets</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {/* Bulk Setting */}
                <div className="p-4 bg-teal-50 dark:bg-teal-900/30 rounded-lg">
                  <h4 className="font-medium text-gray-800 dark:text-white mb-3">Set Same Target for All</h4>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={bulkTargetAmount}
                      onChange={(e) => setBulkTargetAmount(e.target.value)}
                      placeholder="Target amount (PKR)"
                      className="flex-1"
                    />
                    <Button onClick={handleSetBulkTargets} disabled={processing} className="bg-teal-600 hover:bg-teal-700">
                      {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply All'}
                    </Button>
                  </div>
                </div>
                
                {/* Individual Setting */}
                <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
                  <h4 className="font-medium text-gray-800 dark:text-white mb-3">Set Individual Target</h4>
                  <div className="space-y-3">
                    <Select value={individualTarget.employeeId} onValueChange={(v) => setIndividualTarget({ ...individualTarget, employeeId: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetsData?.employees?.map(emp => (
                          <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.employee_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={individualTarget.amount}
                        onChange={(e) => setIndividualTarget({ ...individualTarget, amount: e.target.value })}
                        placeholder="Target amount (PKR)"
                        className="flex-1"
                      />
                      <Button onClick={handleSetIndividualTarget} disabled={processing} variant="outline">
                        {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Target</p>
                <p className="text-2xl font-bold">Rs. {targetsData?.summary?.total_target?.toLocaleString() || 0}</p>
              </div>
              <Target className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Collected</p>
                <p className="text-2xl font-bold">Rs. {targetsData?.summary?.total_collected?.toLocaleString() || 0}</p>
              </div>
              <TrendingUp className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Remaining</p>
                <p className="text-2xl font-bold">Rs. {targetsData?.summary?.total_remaining?.toLocaleString() || 0}</p>
              </div>
              <DollarSign className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Progress</p>
                <p className="text-2xl font-bold">{targetsData?.summary?.overall_progress || 0}%</p>
              </div>
              <Users className="w-10 h-10 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Progress Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Staff Performance - {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {targetsData?.employees?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Employee</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Target</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Collected</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300">Remaining</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-600 dark:text-gray-300 w-48">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {targetsData.employees.map((emp) => (
                    <tr key={emp.employee_id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-800 dark:text-white">{emp.employee_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{emp.department} • {emp.payment_count} payments</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-600 dark:text-gray-300">
                        Rs. {emp.target_amount?.toLocaleString() || '-'}
                      </td>
                      <td className="py-3 px-4 text-right text-green-600 font-medium">
                        Rs. {emp.collected_amount?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-orange-600">
                        Rs. {emp.remaining_amount?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Progress value={emp.progress_percentage} className="h-2 flex-1" />
                          <span className={`text-sm font-medium ${emp.progress_percentage >= 100 ? 'text-green-600' : emp.progress_percentage >= 50 ? 'text-blue-600' : 'text-orange-600'}`}>
                            {emp.progress_percentage}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No targets set for this month</p>
              <p className="text-sm mt-1">Click "Set Targets" to configure monthly goals</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Fees / Reminders */}
      {pendingFees?.students?.length > 0 && (
        <Card className="border-yellow-200 dark:border-yellow-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="w-5 h-5" />
              Pending Fee Collections ({pendingFees.total_pending})
              <Badge className="ml-2 bg-yellow-100 text-yellow-800">Rs. {pendingFees.total_outstanding?.toLocaleString()}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Bulk actions bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-yellow-100 dark:border-yellow-900/40">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                <Checkbox
                  checked={selectedReminderIds.length > 0 && selectedReminderIds.length === pendingFees.students.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-reminders"
                />
                Select all ({selectedReminderIds.length}/{pendingFees.students.length})
              </label>
              <div className="flex items-center gap-2">
                <Dialog open={confirmBulkOpen} onOpenChange={setConfirmBulkOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      disabled={bulkSending}
                      data-testid="bulk-remind-btn"
                    >
                      <Send className="w-4 h-4 mr-1" />
                      {selectedReminderIds.length > 0
                        ? `Remind selected (${selectedReminderIds.length})`
                        : `Remind all (${pendingFees.students.length})`}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send Fee Reminders</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 mt-2 text-sm text-gray-700 dark:text-gray-200">
                      <p>
                        You're about to email a fee reminder to{' '}
                        <strong>
                          {selectedReminderIds.length > 0 ? selectedReminderIds.length : pendingFees.students.length}
                        </strong>{' '}
                        direct {selectedReminderIds.length === 1 || pendingFees.students.length === 1 ? 'student' : 'students'} with outstanding consultancy fees.
                      </p>
                      <p className="text-xs text-gray-500">
                        Each reminder includes the outstanding balance and bank-transfer instructions. The action is logged to the student's Activity Timeline.
                      </p>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="ghost" onClick={() => setConfirmBulkOpen(false)} disabled={bulkSending} data-testid="bulk-remind-cancel">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleBulkRemind}
                          disabled={bulkSending}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          data-testid="bulk-remind-confirm"
                        >
                          {bulkSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                          Send Reminders
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {pendingFees.students.map((student) => (
                <div key={student.student_id} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <label className="flex items-center gap-3 flex-1 cursor-pointer">
                    <Checkbox
                      checked={selectedReminderIds.includes(student.student_id)}
                      onCheckedChange={() => toggleSelectOne(student.student_id)}
                      data-testid={`select-reminder-${student.student_id}`}
                    />
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">{student.student_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {student.destination || 'No destination'} • Remaining: Rs. {student.remaining_amount?.toLocaleString()}
                      </p>
                    </div>
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                    onClick={() => handleSendReminder(student.student_id)}
                    disabled={remindingId === student.student_id}
                    data-testid={`remind-${student.student_id}`}
                  >
                    {remindingId === student.student_id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Bell className="w-4 h-4 mr-1" />
                    )}
                    Remind
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
